import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import type { ValidValueItem } from "../types/api-types";
import ExportCsvButton from "../../shared/export/export-csv-button";
import type { CsvColumn } from "../../shared/export/csv-export";

const exportColumns: CsvColumn<ValidValueItem>[] = [
  { header: "Value", value: (item) => item.value },
];

interface ValidValuesTableProps {
  validValues: ValidValueItem[];
  onEdit: (index: number, item: ValidValueItem) => void;
  onDelete: (id: number, value: string) => void;
}

const ValidValuesTable = ({
  validValues,
  onEdit,
  onDelete,
}: ValidValuesTableProps) => {
  if (validValues.length === 0) {
    return (
      <Box
        sx={{
          p: 4,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 2,
        }}
      >
        <Typography variant="body2" color="text.secondary" textAlign="center">
          No valid values configured
        </Typography>
      </Box>
    );
  }

  return (
    <TableContainer
      sx={{
        maxHeight: 300,
        overflowY: "auto",
        "&::-webkit-scrollbar": {
          display: "none",
        },
        scrollbarWidth: "none",
        msOverflowStyle: "none",
      }}
    >
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow
            sx={{
              "& .MuiTableCell-root": {
                fontWeight: 600,
                color: "primary.main",
                bgcolor: "grey.50",
              },
            }}
          >
            <TableCell>Value</TableCell>
            <TableCell align="right">
              <ExportCsvButton
                rows={validValues}
                columns={exportColumns}
                fileName="valid-values"
              />
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {validValues.map((item, index) => (
            <TableRow
              key={item.id}
              sx={{
                "&:hover": {
                  bgcolor: "action.hover",
                },
                "&:last-child td": {
                  borderBottom: 0,
                },
              }}
            >
              <TableCell sx={{ py: 1.5 }}>{item.value}</TableCell>
              <TableCell align="right" sx={{ py: 1 }}>
                <IconButton
                  size="small"
                  onClick={() => onEdit(index, item)}
                  sx={{
                    color: "text.secondary",
                    "&:hover": {
                      color: "primary.main",
                      bgcolor: "primary.lighter",
                      transform: "scale(1.1)",
                    },
                    transition: "all 0.2s ease-in-out",
                  }}
                  aria-label="Edit valid value"
                >
                  <EditIcon fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() => onDelete(item.id, item.value)}
                  sx={{
                    color: "text.secondary",
                    "&:hover": {
                      color: "error.main",
                      bgcolor: "error.lighter",
                      transform: "scale(1.1)",
                    },
                    transition: "all 0.2s ease-in-out",
                  }}
                  aria-label="Delete valid value"
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default ValidValuesTable;
